'use client';
import React, { useMemo } from 'react';
import type { ShortInterest } from '@benzinga/quotes-manager';
import { formatPercentage } from '@benzinga/utils/numberUtils';
import type { ColumnDef } from '@benzinga/table';
import { formatCount, formatDate } from './formatters';
import QuoteListTable from './QuoteListTable';

type ShortInterestRow = ShortInterest & { settlementDate?: string };

const shortInterestColumnsDef: ColumnDef<ShortInterestRow>[] = [
  {
    field: 'settlementDate',
    headerName: 'Settlement Date',
    valueFormatter: ({ data }) => formatDate(data.settlementDate),
  },
  {
    field: 'recordDate',
    headerName: 'Dissemination Date',
    valueFormatter: ({ data }) => formatDate(data.recordDate),
  },
  {
    cellClass: 'text-right',
    field: 'shortPercentOfFloat',
    headerClass: 'text-right',
    headerName: '% of Float Shorted',
    valueFormatter: ({ data }) => {
      const value = data.shortPercentOfFloat;
      if (value == null) return '—';
      return `${formatPercentage(value)}%`;
    },
  },
  {
    cellClass: 'text-right',
    field: 'sharesFloat',
    headerClass: 'text-right',
    headerName: 'Float',
    valueFormatter: ({ data }) => formatCount(data.sharesFloat),
  },
  {
    cellClass: 'text-right',
    field: 'totalShortInterest',
    headerClass: 'text-right',
    headerName: 'Short Interest',
    valueFormatter: ({ data }) => formatCount(data.totalShortInterest),
  },
  {
    cellClass: 'text-right',
    field: 'averageDailyVolume',
    headerClass: 'text-right',
    headerName: 'Volume',
    valueFormatter: ({ data }) => formatCount(data.averageDailyVolume),
  },
  {
    cellClass: 'text-right',
    field: 'daysToCover',
    headerClass: 'text-right',
    headerName: 'Days to Cover',
    valueFormatter: ({ data }) => (data.daysToCover != null ? String(data.daysToCover) : '—'),
  },
];

export const ShortInterestHistoryTable: React.FC<{ rows: ShortInterestRow[] }> = ({ rows }) => {
  const sortedRows = useMemo(
    () => [...rows].sort((left, right) => (right.settlementDate ?? '').localeCompare(left.settlementDate ?? '')),
    [rows],
  );

  return (
    <QuoteListTable
      columnsDef={shortInterestColumnsDef}
      noDataText="No short interest history available."
      rowData={sortedRows}
    />
  );
};

export default ShortInterestHistoryTable;
